import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DefaultRandomSource,
  DEFAULT_GRADIENT_CONSTRAINTS,
  buildGradient,
  deltaE2000,
  rgb8ToHex,
  rgb8ToUnit,
  rgbUnitToLab,
  scoreColorGuess,
  unitToRgb8,
  hsvToRgbUnit,
  type Difficulty,
  type GradientResult,
  type Hsv,
} from "@hunch/core";
import { GAME_BRANDING } from "@hunch/branding";
import { useI18n } from "../../i18n/index.js";
import { ColorPickerEngine } from "../../components/game/ColorPickerEngine.js";
import { CountdownBar } from "../../components/game/CountdownBar.js";
import { PointsBurst } from "../../components/game/PointsBurst.js";
import type { RoundSummary } from "../../components/game/EndScreen.js";
import { ROUND_TIME_SECONDS } from "../../lib/roundTime.js";
import type { HuessworkConfig } from "./api.js";
import { HuessworkBoxes } from "./HuessworkBoxes.js";
import styles from "./HuessworkPlay.module.css";

const NEUTRAL_GRAY: Hsv = { h: 0, s: 0, v: 0.5 };

const TIMEOUT_ADVANCE_DELAY_MS = 3000;

interface HuessworkPlayProps {
  config: HuessworkConfig;
  difficulty: Difficulty;
  onFinish: (rounds: RoundSummary[], totalScore: number) => void;
}

/** Builds `config.rounds` valid gradients client-side, per spec §9.4 (no DB involvement). */
function buildGame(config: HuessworkConfig): GradientResult[] {
  const rng = new DefaultRandomSource();
  const constraints = {
    ...DEFAULT_GRADIENT_CONSTRAINTS,
    minHueDiff: config.hueDistance.min,
    maxHueDiff: config.hueDistance.max,
    minMidToEndDeltaE: config.minMidToEndDeltaE,
    minMidToPrevMidsDeltaE: config.minMidToEndDeltaE,
  };
  const results: GradientResult[] = [];
  for (let i = 0; i < config.rounds; i++) {
    const gradient = buildGradient(rng, results.map((r) => r.middle), constraints);
    if (gradient) results.push(gradient);
  }
  return results;
}

export function HuessworkPlay({ config, difficulty, onFinish }: HuessworkPlayProps) {
  const { t } = useI18n();
  const roundLimit = ROUND_TIME_SECONDS[difficulty];
  const gradients = useMemo(() => buildGame(config), [config]);
  const [roundIndex, setRoundIndex] = useState(0);
  const [hsv, setHsv] = useState<Hsv>(NEUTRAL_GRAY);
  const [locked, setLocked] = useState(false);
  const [collected, setCollected] = useState<RoundSummary[]>([]);
  const [timeLeft, setTimeLeft] = useState<number | null>(roundLimit);
  const [timedOut, setTimedOut] = useState(false);
  // Reveal defaults to showing the player's own guess in the spectrum (so
  // they can judge how it looks in context first), with a button to switch
  // to the real color on demand — the opposite default of the old
  // continuous-gradient view, per 2026-10 feedback.
  const [showingRealColor, setShowingRealColor] = useState(false);

  const gradient = gradients[roundIndex];
  // Reused from the server's gapWidthPercent config, reinterpreted: instead of
  // a hidden gap width, it's now how much of the gradient the 7 boxes span
  // (see HuessworkBoxes) — narrower span = subtler steps between boxes = easier.
  const spanPercent = config.gapWidthPercent[difficulty];

  const chosenRgb8 = unitToRgb8(hsvToRgbUnit(hsv));

  const deltaE =
    locked && gradient
      ? deltaE2000(rgbUnitToLab(rgb8ToUnit(chosenRgb8)), rgbUnitToLab(rgb8ToUnit(gradient.middle)))
      : 0;
  const points = locked ? scoreColorGuess(deltaE) : 0;

  // Per-round countdown (same mechanic as LineUp, see docs/decisions.md):
  // ticks once per second while still mixing. Pauses once locked in.
  useEffect(() => {
    if (roundLimit === null || locked) return;
    const id = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          setTimedOut(true);
          setLocked(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [locked, roundIndex, roundLimit]);

  function handleNext() {
    const summary: RoundSummary = {
      points,
      maxPoints: 100,
      greenSquares: Math.round(points / 20),
      totalSquares: 5,
    };
    const next = [...collected, summary];
    if (roundIndex + 1 >= gradients.length) {
      onFinish(next, next.reduce((sum, r) => sum + r.points, 0));
      return;
    }
    setCollected(next);
    setRoundIndex(roundIndex + 1);
    setHsv(NEUTRAL_GRAY);
    setLocked(false);
    setTimedOut(false);
    setTimeLeft(roundLimit);
    setShowingRealColor(false);
  }

  // Timed-out rounds auto-advance after a short pause — a manual "Weiter"
  // click in the meantime advances immediately and cancels this.
  useEffect(() => {
    if (!timedOut || !locked) return;
    const id = window.setTimeout(handleNext, TIMEOUT_ADVANCE_DELAY_MS);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timedOut, locked, roundIndex]);

  const runningTotal = collected.reduce((sum, r) => sum + r.points, 0) + (locked ? points : 0);

  if (!gradient) {
    return (
      <main className={styles.statusMain}>
        <p className={styles.error}>{t("huesswork.play.gameLoadError")}</p>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.statusRow}>
        <span className={styles.progress}>
          {GAME_BRANDING.huesswork.name} ·{" "}
          {t("timeslip.play.roundOf", { current: roundIndex + 1, total: gradients.length })}
        </span>
        <span className={styles.totalScore}>{t("timeslip.play.totalScore", { total: runningTotal })}</span>
      </div>
      {timeLeft !== null && roundLimit !== null && (
        <CountdownBar secondsLeft={timeLeft} secondsTotal={roundLimit} />
      )}
      {timedOut && <div className={styles.timeUpBanner}>{t("timeslip.play.timeUp")}</div>}

      {!locked && (
        <>
          <HuessworkBoxes endpoints={gradient} spanPercent={spanPercent} liveColor={chosenRgb8} />
          <ColorPickerEngine
            value={hsv}
            onChange={setHsv}
            hueAriaLabel={t("huesswork.play.hueAriaLabel")}
            areaAriaLabel={t("huesswork.play.areaAriaLabel")}
          />
          <div className={styles.actions}>
            <button type="button" className={styles.primaryButton} onClick={() => setLocked(true)}>
              {t("huesswork.play.lockIn")}
            </button>
          </div>
        </>
      )}

      {locked && (
        <>
          <PointsBurst points={points} triggerKey={roundIndex} />
          <HuessworkBoxes
            endpoints={gradient}
            spanPercent={spanPercent}
            revealed
            altMiddle={showingRealColor ? undefined : chosenRgb8}
          />
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setShowingRealColor((v) => !v)}
            >
              {showingRealColor ? t("huesswork.reveal.viewMyColor") : t("huesswork.reveal.viewRightColor")}
            </button>
          </div>

          <div className={styles.swatches}>
            <div className={styles.swatch}>
              <div className={styles.swatchColor} style={{ backgroundColor: rgb8ToHex(gradient.middle) }} />
              <span className={styles.swatchLabel}>{t("huesswork.reveal.right")}</span>
            </div>
            <div className={styles.swatch}>
              <div className={styles.swatchColor} style={{ backgroundColor: rgb8ToHex(chosenRgb8) }} />
              <span className={styles.swatchLabel}>{t("huesswork.reveal.yourColor")}</span>
            </div>
          </div>

          <p className={styles.points}>+{points}</p>

          <div className={styles.actions}>
            <button type="button" className={styles.primaryButton} onClick={handleNext}>
              {t("timeslip.play.next")}
            </button>
          </div>
        </>
      )}

      <Link to="/" className={styles.cancelLink}>
        {t("timeslip.play.cancelGame")}
      </Link>
    </main>
  );
}
